function isZoomHostname(url: URL) {
  const hostname = url.hostname.toLowerCase();
  return hostname === "zoom.us" || hostname.endsWith(".zoom.us");
}

/**
 * A Zoom host start link, which must never be stored as an attendee destination.
 *
 * `/s/{id}` is the start path, and a `zak` parameter is a host authorization token:
 * either one lets whoever holds the link claim host control of the meeting. The
 * stored link is emailed to every registered attendee, so this is the difference
 * between publishing a join link and publishing the host's credentials.
 */
function isZoomHostStartUrl(url: URL) {
  return url.pathname.startsWith("/s/") || url.searchParams.has("zak");
}

/**
 * Accept only attendee-facing Zoom destinations. Host start URLs, API URLs, and
 * non-Zoom links are deliberately excluded from this organizer-facing field.
 */
export function isZoomJoinUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && isZoomHostname(url) && !isZoomHostStartUrl(url);
  } catch {
    return false;
  }
}

export function normalizeZoomJoinUrl(value: string): string | null {
  if (!isZoomJoinUrl(value)) return null;
  return new URL(value.trim()).toString();
}

/**
 * Reduces what an organizer actually has in hand to a bare Zoom meeting ID.
 *
 * Zoom shows the ID grouped ("123 4567 8901"), Outlook invitations hyphenate it,
 * and the fastest thing to paste is usually the whole join link. All three are
 * accepted; anything that is not 9 to 11 digits is rejected, so the ID can be
 * placed in an API path without escaping ambiguity.
 *
 * Returns null rather than throwing, so the same check can drive a disabled
 * button in the browser and a validation error on the server.
 */
export function normalizeZoomMeetingId(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Deliberately more permissive about the URL than `isZoomJoinUrl`: only the
  // digits survive, and the attendee link is fetched from Zoom afterwards, so a
  // start URL pasted here leaks nothing and still identifies the right meeting.
  const candidate = readMeetingIdFromZoomUrl(trimmed) ?? trimmed.replace(/[\s\-–—]/g, "");

  return /^\d{9,11}$/.test(candidate) ? candidate : null;
}

function readMeetingIdFromZoomUrl(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !isZoomHostname(url)) return null;
    return url.pathname.match(/\/(?:j|w|s|wc\/join)\/(\d{9,11})(?:\/|$)/)?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Groups a bare meeting ID the way Zoom itself displays it, so an organizer can
 * read it back against the Zoom window without counting digits.
 */
export function formatZoomMeetingId(meetingId: string): string {
  const groups: Record<number, number[]> = {
    9: [3, 3, 3],
    10: [3, 3, 4],
    11: [3, 4, 4],
  };
  const pattern = groups[meetingId.length];
  if (!pattern || !/^\d+$/.test(meetingId)) return meetingId;

  let cursor = 0;
  return pattern
    .map(size => meetingId.slice(cursor, (cursor += size)))
    .join(" ");
}

/**
 * Whether a stored attendee link came from local development's simulation rather
 * than from Zoom.
 *
 * Shared because both sides act on it: the server must not ask Zoom to delete a
 * meeting that was never booked, and the workspace must not warn an administrator
 * that a real meeting is about to be deleted when none exists.
 */
export function isSimulatedZoomJoinUrl(joinUrl: string | null | undefined): boolean {
  if (!joinUrl) return false;
  try {
    return new URL(joinUrl).searchParams.get("simulated") === "1";
  } catch {
    return false;
  }
}
